import {
  addLawyerEmails as addLawyerEmailsApi,
  createLawyer as createLawyerApi,
  deleteLawyer as deleteLawyerApi,
  deleteLawyerEmail as deleteLawyerEmailApi,
  getLawyer as getLawyerApi,
  listLawyers as listLawyersApi,
  patchLawyer as patchLawyerApi,
  patchLawyerEmail as patchLawyerEmailApi,
  setFirmRepresentatives as setFirmRepresentativesApi,
} from '@shared/api/lawyers';
import { type Paged, parsePaged } from '@shared/api/paging';
import type {
  LawyerCreateRequest,
  LawyerListQuery,
  LawyerPatchRequest,
} from '@shared/types/lawyer';
import type { Lawyer } from '@domain/lawyer/model';
import {
  type LawyerEmailsAddOutcome,
  parseLawyerEmailsAddResponse,
} from '@domain/lawyer/emails';
import { parseLawyer } from '@domain/lawyer/parseLawyer';

interface ListState {
  data: Paged<Lawyer> | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({ data: null, loading: false, error: null });
let listToken = 0;

export const lawyerListStore = {
  get data(): Paged<Lawyer> | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  async refresh(query: LawyerListQuery): Promise<void> {
    const token = ++listToken;
    listState.loading = true;
    listState.error = null;
    try {
      const raw = await listLawyersApi(query);
      if (token !== listToken) return;
      listState.data = parsePaged(raw, parseLawyer);
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
};

interface DetailState {
  data: Lawyer | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({ data: null, loading: false, error: null });
let detailToken = 0;

export const lawyerDetailStore = {
  get data(): Lawyer | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const raw = await getLawyerApi(id);
      if (token !== detailToken) return;
      detailState.data = parseLawyer(raw);
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(lawyer: Lawyer): void {
    detailState.data = lawyer;
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

/// ある事務所に所属する弁護士一覧を保持するストア。
///
/// 事務所詳細 / 弁護士詳細ページで、契約対象・タイムライン参加者の id → 名前解決や
/// モーダルの選択肢に使う。メインの一覧 (`lawyerListStore`) とは状態を共有させたくない
/// ため別インスタンスにしている。`firmId` が未設定 (null) のときは空配列にする。
interface FirmLawyersState {
  data: Lawyer[];
  loading: boolean;
  error: string | null;
}

const firmLawyersState = $state<FirmLawyersState>({ data: [], loading: false, error: null });
let firmLawyersToken = 0;

export const firmLawyersStore = {
  get data(): Lawyer[] {
    return firmLawyersState.data;
  },
  get loading(): boolean {
    return firmLawyersState.loading;
  },
  get error(): string | null {
    return firmLawyersState.error;
  },
  /// 指定事務所の所属弁護士を full_name 昇順で**全件**取得する。
  /// backend の page_size 上限が 200 のため全ページをループで集める
  /// (代表弁護士の候補に全所属弁護士を出すため — spec
  /// 20260612-multiple-firm-representative-lawyers。 201 件目以降が候補から
  /// 漏れないようにする)。 暴走防止に 50 ページ (10,000 名) で打ち切る。
  /// stale レスポンスは token で破棄。
  async load(firmId: string | null): Promise<void> {
    const token = ++firmLawyersToken;
    if (!firmId) {
      firmLawyersState.data = [];
      firmLawyersState.loading = false;
      firmLawyersState.error = null;
      return;
    }
    firmLawyersState.loading = true;
    firmLawyersState.error = null;
    try {
      const pageSize = 200;
      const maxPages = 50;
      const all: Lawyer[] = [];
      for (let page = 1; page <= maxPages; page++) {
        const raw = await listLawyersApi({
          firm_id: firmId,
          page,
          page_size: pageSize,
          sort_by: 'full_name',
          sort_dir: 'asc',
        });
        if (token !== firmLawyersToken) return;
        const parsed = parsePaged(raw, parseLawyer);
        all.push(...parsed.items);
        if (all.length >= parsed.total || parsed.items.length === 0) break;
      }
      firmLawyersState.data = all;
    } catch (err) {
      if (token !== firmLawyersToken) return;
      firmLawyersState.data = [];
      firmLawyersState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === firmLawyersToken) firmLawyersState.loading = false;
    }
  },
  clear(): void {
    firmLawyersToken++;
    firmLawyersState.data = [];
    firmLawyersState.loading = false;
    firmLawyersState.error = null;
  },
};

export async function createLawyer(body: LawyerCreateRequest): Promise<Lawyer> {
  return parseLawyer(await createLawyerApi(body));
}

export async function patchLawyer(id: string, body: LawyerPatchRequest): Promise<Lawyer> {
  const updated = parseLawyer(await patchLawyerApi(id, body));
  if (detailState.data?.id === id) {
    detailState.data = updated;
  }
  return updated;
}

export async function deleteLawyer(id: string): Promise<void> {
  await deleteLawyerApi(id);
  if (detailState.data?.id === id) {
    detailState.data = null;
  }
}

/// メール mutation 後の Lawyer (レスポンス同梱) で詳細ストアを丸ごと置き換える。
/// emails だけの部分更新にしない — メール変更で lawyers.updated_at が進むため、
/// ヘッダーの「更新」表示等を stale にしない (旧 PATCH 経路と同じ同期粒度)。
function syncDetailLawyer(id: string, lawyer: Lawyer): void {
  if (detailState.data?.id === id) {
    detailState.data = lawyer;
  }
}

/// メール追加 (1..n 件、入力順末尾追加)。値ごとの成否と mutation 後の Lawyer を
/// 返し、詳細ストアも同期する (CU-86eyaj73w)。
export async function addLawyerEmails(
  id: string,
  emails: string[],
): Promise<LawyerEmailsAddOutcome> {
  const outcome = parseLawyerEmailsAddResponse(await addLawyerEmailsApi(id, { emails }));
  syncDetailLawyer(id, outcome.lawyer);
  return outcome;
}

/// メール編集 (値のみ変更、表示順は不変)。重複は backend が 409 で拒否する。
export async function updateLawyerEmail(
  id: string,
  emailId: string,
  email: string,
): Promise<Lawyer> {
  const lawyer = parseLawyer(await patchLawyerEmailApi(id, emailId, { email }));
  syncDetailLawyer(id, lawyer);
  return lawyer;
}

/// メール削除 (表示順は詰めない)。
export async function deleteLawyerEmail(id: string, emailId: string): Promise<Lawyer> {
  const lawyer = parseLawyer(await deleteLawyerEmailApi(id, emailId));
  syncDetailLawyer(id, lawyer);
  return lawyer;
}

/// 代表弁護士集合の全置換 (spec 20260612-multiple-firm-representative-lawyers)。
/// `lawyerIds: []` は「代表 0 名」 として正常に保存される。 置換後の代表を
/// 氏名 (full_name, id) の決定的順序で返す (表示順は保存値ではない)。
export async function setFirmRepresentatives(
  firmId: string,
  lawyerIds: string[],
): Promise<Lawyer[]> {
  const raw = await setFirmRepresentativesApi(firmId, { lawyer_ids: lawyerIds });
  const body = raw as { representative_lawyers?: unknown };
  const items = Array.isArray(body?.representative_lawyers) ? body.representative_lawyers : [];
  return items.map((item) => parseLawyer(item));
}
